// Import the zustand store instance
import {
  deformatDateT,
  deformatMonthT,
  deformatYearT,
  formatDateT,
} from "../../../shared/utils/formatters";
import useQuoteStore from "../store/QuoteStore";
import { updateQuoteFields } from "../../../shared/utils/storeUtils";
import useAppStateStore from "../../../shared/store/AppStateStore";

const Tq5Mapper = {
  get() {
    const vehicle = useQuoteStore.getState().quote?.risk?.vehicles?.[0] ?? {};
    const securityDevice = vehicle?.securityDevices?.[0] ?? null;
    const modification = vehicle?.modifications?.[0] ?? null;
    const hasVehicleData = vehicle?.make && vehicle?.model;
    const STATE = useAppStateStore.getState();

    return {
      VehicleReg:
        vehicle?.registrationNumber ?? vehicle?.licensingPlateNumber ?? null,
      make: vehicle?.make ?? null,
      model: vehicle?.model ?? null,
      abiCode: vehicle?.abiCode ?? null,
      noOfSeats: vehicle?.noOfSeats ?? null,
      cubicCapacity: vehicle?.cubicCapacity ?? null,
      yearOfManufacture: vehicle?.yearOfManufacture ?? null,
      bodyTypeId: vehicle?.bodyTypeId ?? null,
      manualBodyTypeId: STATE.manualBodyTypeId ?? null,
      fuelTypeId: vehicle?.fuelTypeId ?? null,
      gearboxId: vehicle?.gearboxId ?? null,
      registeredDate: vehicle?.registeredDate ?? null,
      preVehicleSearch:
        vehicle?.preVehicleSearch ??
        (hasVehicleData
          ? `${vehicle.yearOfManufacture}, ${vehicle.cubicCapacity}cc, ${vehicle.noOfSeats} seats`
          : null),
      vehImported:
        vehicle?.imported === null ? null : vehicle.imported ? "Yes" : "No",
      vehicleQPlated:
        vehicle?.qPlated === null ? null : vehicle.qPlated ? "Yes" : "No",
      rightHandDrive:
        vehicle?.rightHandDrive === null
          ? null
          : vehicle.rightHandDrive
          ? "Yes"
          : "No",
      seats: vehicle?.noOfSeats?.toString() ?? null,
      vehicleModified:
        vehicle?.modifications && vehicle.modifications.length > 0
          ? "Yes"
          : vehicle?.selfModified === null
          ? null
          : vehicle.selfModified
          ? "Yes"
          : "No",
      modifications: modification?.vehicleModificationId
        ? { value: modification.vehicleModificationId }
        : null,
      dashCam:
        vehicle?.interiorCamera === null
          ? null
          : vehicle.interiorCamera
          ? "Yes"
          : "No",
      security: vehicle?.securityDevices
        ? vehicle.securityDevices.length > 0
          ? "Yes"
          : "No"
        : null,
      securityDevice: securityDevice?.securityDeviceType
        ? { value: securityDevice.securityDeviceType }
        : null,
      vehiclePurchased:
        vehicle?.purchaseDate === null
          ? null
          : deformatDateT(vehicle.purchaseDate) === "30/12/1899"
          ? "No"
          : "Yes",
      purchaseDateMonth:
        vehicle?.purchaseDate &&
        deformatDateT(vehicle.purchaseDate) !== "30/12/1899"
          ? { value: deformatMonthT(vehicle.purchaseDate) }
          : null,
      purchaseDateYear:
        vehicle?.purchaseDate &&
        deformatDateT(vehicle.purchaseDate) !== "30/12/1899"
          ? { value: deformatYearT(vehicle.purchaseDate) }
          : null,
      vehRegistration:
        vehicle?.havePreviousRegistration === null
          ? null
          : vehicle.havePreviousRegistration
          ? "Yes"
          : "No",
      ownedPrevVehicle:
        vehicle?.previousVehicleOwned12Month === null
          ? null
          : vehicle.previousVehicleOwned12Month
          ? "Yes"
          : "No",
    };
  },

  set(data) {
    const updateFields = {};
    const STATE = useAppStateStore.getState();
    const quote = useQuoteStore.getState().quote ?? {};

    // Vehicle Registration
    if (data.manualBodyTypeId) {
      STATE.setManualBodyTypeId(data.manualBodyTypeId);
    }

    if (data.VehicleReg) {
      updateFields["risk.vehicles.0.licensingPlateNumber"] = data.VehicleReg;
      updateFields["risk.vehicles.0.registrationNumber"] = data.VehicleReg;
    } else {
      updateFields["risk.vehicles.0.licensingPlateNumber"] = null;
      updateFields["risk.vehicles.0.registrationNumber"] = null;
    }

    // Vehicle Lookup Data
    if (data.make) {
      updateFields["risk.vehicles.0.make"] = data.make;
      updateFields["risk.vehicles.0.model"] = data.model;
      updateFields["risk.vehicles.0.abiCode"] = data.abiCode;
      updateFields["risk.vehicles.0.noOfSeats"] = data.noOfSeats;
      updateFields["risk.vehicles.0.cubicCapacity"] = data.cubicCapacity;
      updateFields["risk.vehicles.0.yearOfManufacture"] =
        data.yearOfManufacture;
      updateFields["risk.vehicles.0.bodyTypeId"] =
        data.bodyTypeId ||
        (data.manualBodyTypeId
          ? data.manualBodyTypeId.value
          : STATE.manualBodyTypeId?.value);
      updateFields["risk.vehicles.0.fuelTypeId"] = data.fuelTypeId;
      updateFields["risk.vehicles.0.gearboxId"] = data.gearboxId;
      updateFields["risk.vehicles.0.qPlated"] = data.qPlated;
      updateFields["risk.vehicles.0.registeredDate"] = data.registeredDate;
      updateFields["risk.vehicles.0.imported"] = data.imported;
      updateFields["risk.vehicles.0.rightHandDrive"] = data.rightHandDrive;
      updateFields["risk.vehicles.0.preVehicleSearch"] = data.preVehicleSearch;
    }

    // Basic Vehicle Details
    if (data.vehImported !== null)
      updateFields["risk.vehicles.0.imported"] = data.vehImported === "Yes";
    if (data.vehicleQPlated !== null)
      updateFields["risk.vehicles.0.qPlated"] = data.vehicleQPlated === "Yes";
    if (data.rightHandDrive !== null)
      updateFields["risk.vehicles.0.rightHandDrive"] =
        data.rightHandDrive === "Yes";
    if (data.seats)
      updateFields["risk.vehicles.0.noOfSeats"] = parseInt(data.seats);
    if (data.vehicleModified !== null)
      updateFields["risk.vehicles.0.selfModified"] =
        data.vehicleModified === "Yes";
    if (data.dashCam !== null)
      updateFields["risk.vehicles.0.interiorCamera"] = data.dashCam === "Yes";

    // Modifications
    if (data.vehicleModified === "Yes" && data.modifications?.value) {
      updateFields["risk.vehicles.0.modifications"] = [
        {
          typeId: "4",
          vehicleModificationId: data.modifications.value,
          objectId: "",
          objectHistoryId: 0,
        },
      ];
    } else if (data.vehicleModified === "No") {
      updateFields["risk.vehicles.0.modifications"] = null;
    }

    // Security Devices
    if (data.security === "Yes" && data.securityDevice?.value) {
      updateFields["risk.vehicles.0.securityDevices"] = [
        {
          typeId: "3",
          securityDeviceType: data.securityDevice.value,
          objectId: "",
          objectHistoryId: 0,
        },
      ];
    } else if (data.security === "No") {
      updateFields["risk.vehicles.0.securityDevices"] = [];
    }

    // Purchase Info
    if (
      data.vehiclePurchased === "Yes" &&
      data.purchaseDateYear &&
      data.purchaseDateMonth
    ) {
      const purchaseDate = formatDateT(
        `${data.purchaseDateYear.value}/${data.purchaseDateMonth.value}/01`,
      );
      updateFields["risk.vehicles.0.purchaseDate"] = purchaseDate;
    } else if (data.vehiclePurchased === "No") {
      updateFields["risk.vehicles.0.purchaseDate"] =
        quote?.risk?.startDate ?? formatDateT("1899/12/31");
    }

    // Previous Vehicle
    if (data.vehRegistration !== null)
      updateFields["risk.vehicles.0.havePreviousRegistration"] =
        data.vehRegistration === "Yes";
    if (data.ownedPrevVehicle !== null)
      updateFields["risk.vehicles.0.previousVehicleOwned12Month"] =
        data.ownedPrevVehicle === "Yes";

    updateQuoteFields(updateFields);
  },
};

export default Tq5Mapper;
